import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAdminStore } from '../admin/store/adminStore';
import { formatPrice } from '../data/products';
import { ORDER_STATUS_LABELS, ORDER_STATUS_COLORS, OrderStatus } from '../admin/types/admin';
import {
  Package, Truck, CheckCircle, Clock, XCircle, MapPin, ChevronDown, ChevronUp,
  RotateCcw, X, Send, AlertTriangle
} from 'lucide-react';

const STATUS_TIMELINE: { status: OrderStatus; icon: React.ReactNode; label: string }[] = [
  { status: 'pending', icon: <Clock className="w-4 h-4" />, label: 'Sipariş Alındı' },
  { status: 'processing', icon: <Package className="w-4 h-4" />, label: 'Hazırlanıyor' },
  { status: 'shipped', icon: <Truck className="w-4 h-4" />, label: 'Kargoya Verildi' },
  { status: 'in_transit', icon: <MapPin className="w-4 h-4" />, label: 'Yolda' },
  { status: 'delivered', icon: <CheckCircle className="w-4 h-4" />, label: 'Teslim Edildi' },
];

const ShippingTrackingPage: React.FC = () => {
  const { t } = useTranslation();
  const { orders, requestReturn, fetchOrdersFromAPI } = useAdminStore();
  const [orderNumber, setOrderNumber] = useState('');
  const [email, setEmail] = useState('');
  const [searched, setSearched] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [foundOrder, setFoundOrder] = useState(null as any);
  const [showDetails, setShowDetails] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [returnReason, setReturnReason] = useState('');
  const [returnDescription, setReturnDescription] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');

  // Sayfa açıldığında sipariş listesini backend ile senkronize et, aksi halde
  // bu sekmede daha önce admin paneli açılmadıysa gerçek siparişler görünmez.
  useEffect(() => {
    fetchOrdersFromAPI();
  }, [fetchOrdersFromAPI]);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orderNumber.trim() || !email.trim()) return;

    setSearching(true);
    setSearchError('');
    // Aramadan hemen önce güncel siparişleri backend'den çek, böylece başka bir
    // cihazdan/tarayıcıdan verilen sipariş de bulunabilsin.
    await fetchOrdersFromAPI();
    setSearching(false);

    // Güvenlik: sadece sipariş numarasıyla değil, siparişte kayıtlı e-posta
    // adresiyle birlikte eşleştiriyoruz. Aksi halde sipariş numarasını tahmin
    // eden/öğrenen herkes başkasının siparişini görüntüleyip iptal edebilirdi.
    const found = useAdminStore.getState().orders.find(
      o => o.orderNumber.toLowerCase() === orderNumber.trim().toLowerCase()
        && o.customerEmail?.toLowerCase() === email.trim().toLowerCase()
    );

    if (!found) {
      // Sipariş numarası var ama e-posta eşleşmiyorsa bile aynı genel hatayı
      // veriyoruz; hangisinin yanlış olduğunu belirtmek bilgi sızdırır.
      setSearchError('Sipariş numarası veya e-posta adresi hatalı. Lütfen sipariş onay e-postanızı kontrol edin.');
    }

    setFoundOrder(found || null);
    setSearched(true);
    setShowDetails(false);
    setActionSuccess('');
  };

  const getTimelineIndex = (status: OrderStatus): number => {
    return STATUS_TIMELINE.findIndex(s => s.status === status);
  };

  const handleReturnRequest = () => {
    if (!foundOrder || !returnReason.trim()) return;
    requestReturn(foundOrder.id, returnReason, returnDescription);
    setShowReturnModal(false);
    setReturnReason('');
    setReturnDescription('');
    setActionSuccess('İade talebiniz başarıyla oluşturuldu. Admin tarafından değerlendirilecektir.');

    // Refresh the found order
    const updated = orders.find(o => o.id === foundOrder.id);
    if (updated) setFoundOrder(updated);
  };

  const handleCancelOrder = () => {
    if (!foundOrder) return;
    const { updateOrderStatus } = useAdminStore.getState();
    updateOrderStatus(foundOrder.id, 'cancelled');
    setShowCancelModal(false);
    setActionSuccess('Siparişiniz başarıyla iptal edildi.');

    // Refresh
    const updated = useAdminStore.getState().orders.find(o => o.id === foundOrder.id);
    if (updated) setFoundOrder(updated);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0A0A0A] to-[#1a1a2e] text-white py-10 sm:py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl sm:text-4xl font-black">📦 {t('nav.tracking')}</h1>
          <p className="text-gray-400 mt-2">Sipariş numaranız ile kargonuzu takip edin</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Search Form */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 sm:p-8 shadow-sm">
          <form onSubmit={handleSearch} className="space-y-6">
            <div>
              <label className="block text-sm font-bold text-gray-800 mb-2">Sipariş Numaranız</label>
              <input
                type="text"
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                placeholder="Örn: TRX2607200001"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#E31E24] focus:outline-none transition-colors text-sm"
              />
              <p className="text-xs text-gray-400 mt-2">
                Sipariş numaranız e-posta ve SMS ile gönderilmiştir.
              </p>
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-800 mb-2">E-posta Adresiniz</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Siparişte kullandığınız e-posta adresi"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#E31E24] focus:outline-none transition-colors text-sm"
              />
              <p className="text-xs text-gray-400 mt-2">
                Güvenlik amacıyla, siparişinizi görüntüleyebilmek için sipariş numaranızla birlikte e-posta adresinizi de doğrulamamız gerekiyor.
              </p>
            </div>
            <button
              type="submit"
              disabled={searching}
              className="w-full py-3 bg-gradient-to-r from-[#E31E24] to-[#FF4500] text-white font-bold rounded-xl hover:shadow-lg hover:shadow-[#E31E24]/30 transition-all duration-300 uppercase tracking-wider text-sm disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {searching ? '⏳ Aranıyor...' : '📦 Kargomu Takip Et'}
            </button>
          </form>
        </div>

        {/* Success Message */}
        {actionSuccess && (
          <div className="mt-6 bg-green-50 border border-green-200 rounded-2xl p-6">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 shrink-0" />
              <p className="text-sm text-green-700 font-medium">{actionSuccess}</p>
            </div>
          </div>
        )}

        {/* Search Results */}
        {searched && (
          <div className="mt-6">
            {!foundOrder ? (
              <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center shadow-sm">
                <div className="text-5xl mb-4">❌</div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">Sipariş Bulunamadı</h3>
                <p className="text-sm text-gray-500">
                  {searchError || 'Lütfen sipariş numaranızı ve e-posta adresinizi kontrol edin.'}
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Order Status Card */}
                <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-lg font-bold text-gray-800">Sipariş: {foundOrder.orderNumber}</h3>
                      <p className="text-xs text-gray-400 mt-1">
                        Tarih: {new Date(foundOrder.createdAt).toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' })}
                      </p>
                    </div>
                    <span className={`px-3 py-1.5 rounded-full text-xs font-semibold ${ORDER_STATUS_COLORS[foundOrder.status]}`}>
                      {ORDER_STATUS_LABELS[foundOrder.status]}
                    </span>
                  </div>

                  {/* Timeline */}
                  <div className="relative">
                    {STATUS_TIMELINE.map((item, index) => {
                      const currentIndex = getTimelineIndex(foundOrder.status);
                      const isCompleted = index <= currentIndex;
                      const isCurrent = index === currentIndex;
                      const isCancelled = foundOrder.status === 'cancelled' || foundOrder.status === 'returned';

                      return (
                        <div key={item.status} className="flex items-start gap-4 pb-6 last:pb-0 relative">
                          {/* Line */}
                          {index < STATUS_TIMELINE.length - 1 && (
                            <div className={`absolute left-4 top-8 w-0.5 h-full ${
                              isCompleted && !isCancelled ? 'bg-[#E31E24]' : 'bg-gray-200'
                            }`} />
                          )}

                          {/* Circle */}
                          <div className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                            isCompleted && !isCancelled
                              ? isCurrent
                                ? 'bg-[#E31E24] text-white shadow-lg shadow-[#E31E24]/30'
                                : 'bg-green-500 text-white'
                              : 'bg-gray-200 text-gray-400'
                          }`}>
                            {item.icon}
                          </div>

                          {/* Content */}
                          <div className="flex-1 pt-1">
                            <p className={`text-sm font-semibold ${
                              isCompleted && !isCancelled ? 'text-gray-800' : 'text-gray-400'
                            }`}>
                              {item.label}
                            </p>
                            {isCurrent && !isCancelled && (
                              <p className="text-xs text-gray-400 mt-0.5">Mevcut durum</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Cancelled/Returned Status */}
                  {(foundOrder.status === 'cancelled' || foundOrder.status === 'returned' || foundOrder.status === 'return_requested') && (
                    <div className={`mt-4 p-4 rounded-xl ${
                      foundOrder.status === 'cancelled' ? 'bg-red-50 border border-red-200' :
                      foundOrder.status === 'return_requested' ? 'bg-amber-50 border border-amber-200' :
                      'bg-orange-50 border border-orange-200'
                    }`}>
                      <div className="flex items-center gap-2">
                        {foundOrder.status === 'cancelled' ? (
                          <XCircle className="w-4 h-4 text-red-600" />
                        ) : (
                          <AlertTriangle className="w-4 h-4 text-amber-600" />
                        )}
                        <p className={`text-sm font-semibold ${
                          foundOrder.status === 'cancelled' ? 'text-red-700' :
                          foundOrder.status === 'return_requested' ? 'text-amber-700' :
                          'text-orange-700'
                        }`}>
                          {ORDER_STATUS_LABELS[foundOrder.status]}
                        </p>
                      </div>
                      {foundOrder.returnRequest && (
                        <div className="mt-2 text-xs text-gray-600">
                          <p>Sebep: {foundOrder.returnRequest.reason}</p>
                          {foundOrder.returnRequest.description && (
                            <p className="mt-1">Açıklama: {foundOrder.returnRequest.description}</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Order Details Toggle */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                  <button
                    onClick={() => setShowDetails(!showDetails)}
                    className="w-full flex items-center justify-between p-6 text-left"
                  >
                    <span className="text-sm font-bold text-gray-800">Sipariş Detayları</span>
                    {showDetails ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                  </button>

                  {showDetails && (
                    <div className="px-6 pb-6 space-y-4 border-t border-gray-100 pt-4">
                      {/* Items */}
                      <div className="space-y-2">
                        {foundOrder.items.map((item: any, idx: number) => (
                          <div key={idx} className="flex items-center gap-3 bg-gray-50 rounded-xl p-3">
                            <img src={item.product.images[0]} alt={item.product.name} className="w-10 h-10 object-contain bg-white rounded-lg" />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold text-gray-800 truncate">{item.product.name}</p>
                              <p className="text-xs text-gray-400">{item.quantity} adet</p>
                            </div>
                            <span className="text-xs font-bold">
                              {formatPrice((item.product.salePrice > 0 ? item.product.salePrice : item.product.regularPrice) * item.quantity)} ₺
                            </span>
                          </div>
                        ))}
                      </div>

                      {/* Summary */}
                      <div className="border-t border-gray-100 pt-4 space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Ara Toplam</span>
                          <span className="font-semibold">{formatPrice(foundOrder.totalAmount - foundOrder.shippingCost)} ₺</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Kargo</span>
                          <span className={`font-semibold ${foundOrder.shippingCost === 0 ? 'text-green-600' : ''}`}>
                            {foundOrder.shippingCost === 0 ? 'Ücretsiz' : `${formatPrice(foundOrder.shippingCost)} ₺`}
                          </span>
                        </div>
                        <div className="border-t border-gray-100 pt-2 flex justify-between">
                          <span className="text-sm font-bold text-gray-800">Toplam</span>
                          <span className="text-sm font-black text-[#E31E24]">{formatPrice(foundOrder.totalAmount)} ₺</span>
                        </div>
                      </div>

                      {/* Address */}
                      <div className="border-t border-gray-100 pt-4">
                        <p className="text-xs font-semibold text-gray-600 mb-1">Teslimat Adresi</p>
                        <p className="text-sm text-gray-700">{foundOrder.shippingAddress}, {foundOrder.district}, {foundOrder.city}</p>
                      </div>

                      {/* Customer Info */}
                      <div>
                        <p className="text-xs font-semibold text-gray-600 mb-1">Müşteri Bilgileri</p>
                        <p className="text-sm text-gray-700">{foundOrder.customerName}</p>
                        <p className="text-xs text-gray-400">{foundOrder.customerEmail} | {foundOrder.customerPhone}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                {(foundOrder.status === 'delivered' || foundOrder.status === 'in_transit' || foundOrder.status === 'shipped' || foundOrder.status === 'pending' || foundOrder.status === 'processing') && (
                  <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
                    <h3 className="text-sm font-bold text-gray-800 mb-4">İşlemler</h3>
                    <div className="flex flex-wrap gap-3">
                      {foundOrder.status === 'delivered' && !foundOrder.returnRequest && (
                        <button
                          onClick={() => setShowReturnModal(true)}
                          className="flex items-center gap-2 px-4 py-2.5 bg-orange-50 text-orange-600 rounded-xl text-sm font-semibold hover:bg-orange-100 transition-colors border border-orange-200"
                        >
                          <RotateCcw className="w-4 h-4" />
                          İade Talebi Oluştur
                        </button>
                      )}
                      {(foundOrder.status === 'pending' || foundOrder.status === 'processing') && (
                        <button
                          onClick={() => setShowCancelModal(true)}
                          className="flex items-center gap-2 px-4 py-2.5 bg-red-50 text-red-600 rounded-xl text-sm font-semibold hover:bg-red-100 transition-colors border border-red-200"
                        >
                          <XCircle className="w-4 h-4" />
                          Siparişi İptal Et
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Return Modal */}
        {showReturnModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setShowReturnModal(false)}>
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-bold text-gray-800">🔄 İade Talebi</h2>
                  <button onClick={() => setShowReturnModal(false)} className="p-2 hover:bg-gray-100 rounded-full">
                    <X className="w-5 h-5 text-gray-400" />
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1">{foundOrder?.orderNumber} siparişi için iade talebi</p>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">İade Sebebi *</label>
                  <select
                    value={returnReason}
                    onChange={(e) => setReturnReason(e.target.value)}
                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-[#E31E24] focus:outline-none text-sm"
                  >
                    <option value="">Seçiniz...</option>
                    <option value="Yanlış ürün">Yanlış ürün gönderildi</option>
                    <option value="Hasarlı ürün">Hasarlı/Bozuk ürün</option>
                    <option value="Eksik ürün">Eksik ürün</option>
                    <option value="Beklentiyi karşılamıyor">Ürün beklentiyi karşılamıyor</option>
                    <option value="Fikir değişikliği">Fikir değişikliği</option>
                    <option value="Diğer">Diğer</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Açıklama</label>
                  <textarea
                    value={returnDescription}
                    onChange={(e) => setReturnDescription(e.target.value)}
                    placeholder="İade sebebinizi açıklayınız..."
                    rows={3}
                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-[#E31E24] focus:outline-none text-sm resize-none"
                  />
                </div>
              </div>
              <div className="p-6 border-t border-gray-100 flex gap-3">
                <button
                  onClick={() => setShowReturnModal(false)}
                  className="flex-1 py-2.5 bg-gray-100 text-gray-600 font-semibold rounded-xl text-sm hover:bg-gray-200 transition-colors"
                >
                  Vazgeç
                </button>
                <button
                  onClick={handleReturnRequest}
                  disabled={!returnReason.trim()}
                  className="flex-1 py-2.5 bg-[#E31E24] text-white font-semibold rounded-xl text-sm hover:bg-[#c41a1f] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <Send className="w-4 h-4" /> Gönder
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Cancel Modal */}
        {showCancelModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setShowCancelModal(false)}>
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-bold text-gray-800">⚠️ Sipariş İptali</h2>
                  <button onClick={() => setShowCancelModal(false)} className="p-2 hover:bg-gray-100 rounded-full">
                    <X className="w-5 h-5 text-gray-400" />
                  </button>
                </div>
              </div>
              <div className="p-6">
                <p className="text-sm text-gray-600">
                  <span className="font-bold">{foundOrder?.orderNumber}</span> numaralı siparişinizi iptal etmek istediğinize emin misiniz?
                </p>
                <p className="text-xs text-gray-400 mt-2">Bu işlem geri alınamaz.</p>
              </div>
              <div className="p-6 border-t border-gray-100 flex gap-3">
                <button
                  onClick={() => setShowCancelModal(false)}
                  className="flex-1 py-2.5 bg-gray-100 text-gray-600 font-semibold rounded-xl text-sm hover:bg-gray-200 transition-colors"
                >
                  Vazgeç
                </button>
                <button
                  onClick={handleCancelOrder}
                  className="flex-1 py-2.5 bg-red-600 text-white font-semibold rounded-xl text-sm hover:bg-red-700 transition-colors"
                >
                  İptal Et
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShippingTrackingPage;
